/**
 * Zweistufige Filterleiste im Kurs-Archiv (LernWelt).
 *
 * Serverseitig gerendert von \Yours\CourseFilters. Die Komponente macht die Chips
 * klickbar: Mehrfachauswahl je Ebene, gegenseitiges AND zwischen den Ebenen, "Alle
 * löschen" und Zurück/Vorwärts.
 *
 * Sie kennt die beiden Filterebenen nicht namentlich. Jede Gruppe sagt über
 * data-param, in welches Hidden-Input ihre Auswahl geschrieben wird, und über
 * data-value-attr, welches data-Attribut des Chips den Wert liefert (term-ID bei
 * filter-block-tags, Slug bei filter-categories). Eine dritte Ebene bräuchte hier
 * deshalb keine Zeile.
 *
 * Das Nachladen der Kursliste, die Trefferzahl und die URL übernimmt BuddyBoss'
 * BBLMS.fetchCourses(), das das ganze Formular serialisiert — es gibt kein eigenes
 * Request-Handling. Drei Eigenheiten sind dabei zu beachten:
 *
 * 1. Kein change-Event. BuddyBoss lauscht delegiert auf change bei
 *    [name='filter-categories']; unser Hidden-Input trägt genau diesen Namen, ein
 *    dispatch würde also einen zweiten Request auslösen. Wir rufen direkt auf.
 * 2. Pagination selbst zurücksetzen. BuddyBoss' resetting_fields kennt
 *    filter-block-tags nicht, deshalb setzen wir current_page bei jedem Klick auf 1.
 * 3. Zurück/Vorwärts selbst bedienen, siehe restore().
 */
export default function courseFilters() {
    const container = document.querySelector(".ae-course-filters");
    const form = document.querySelector("#bb-courses-directory-form");

    if (!container || !form) {
        return;
    }

    const list = form.querySelector(".bs-dir-list");
    const counter = form.querySelector(".bb-item-count");
    const clearButton = container.querySelector(".ae-course-filters__clear");
    const pageInput = form.querySelector('[name="current_page"]');

    const groups = Array.from(container.querySelectorAll(".ae-course-filters__group"))
        .map((element) => ({
            param: element.dataset.param,
            valueAttr: element.dataset.valueAttr,
            // Map-Richtung: zeigt von den Werten der anderen Ebene auf meine.
            mapName: element.dataset.map,
            input: form.querySelector(
                'input[type="hidden"][name="' + element.dataset.param + '"]'
            ),
            chips: Array.from(element.querySelectorAll(".ae-course-chip")),
        }))
        .filter((group) => group.param && group.valueAttr && group.input);

    if (!groups.length) {
        return;
    }

    // Ko-Vorkommens-Map, serverseitig neben der Leiste ausgegeben. Sie hängt nicht von
    // der Auswahl ab und wird deshalb einmal gelesen — BuddyBoss tauscht beim Filtern
    // ohnehin nur die Kursliste aus, nicht die Leiste.
    const mapElement = container.querySelector(".ae-course-filters__map");
    let map = {};

    try {
        map = JSON.parse(mapElement.textContent) || {};
    } catch (error) {
        map = {};
    }

    const valueOf = (group, chip) => String(chip.dataset[group.valueAttr]);

    /**
     * Der DOM ist die einzige Quelle der Auswahl.
     *
     * aria-pressed muss ohnehin gepflegt werden, und beim Zurück-Sprung wird die
     * Auswahl komplett neu gesetzt — eine zweite Liste daneben könnte nur auseinander
     * laufen.
     */
    const selectionOf = (group) =>
        group.chips
            .filter((chip) => chip.getAttribute("aria-pressed") === "true")
            .map((chip) => valueOf(group, chip));

    const applySelection = (group, values) => {
        const wanted = values.map(String);

        group.chips.forEach((chip) => {
            const isActive = wanted.indexOf(valueOf(group, chip)) !== -1;

            chip.setAttribute("aria-pressed", isActive ? "true" : "false");
            chip.classList.toggle("is-active", isActive);
        });
    };

    /**
     * Auswahl in die Hidden-Inputs schreiben, die BuddyBoss serialisiert.
     *
     * Leerer Zustand ist der leere String — in beiden Ebenen. Ein 'all' wie beim alten
     * Dropdown darf hier nicht landen: wp_parse_id_list('all') ergibt [0] und damit
     * garantiert kein Ergebnis.
     */
    const syncInputs = () => {
        let hasSelection = false;

        groups.forEach((group) => {
            const values = selectionOf(group);

            group.input.value = values.join(",");

            if (values.length) {
                hasSelection = true;
            }
        });

        if (clearButton) {
            clearButton.hidden = !hasSelection;
        }
    };

    /**
     * Welche Werte einer Ebene die Auswahl der anderen Ebenen noch zulässt.
     *
     * Vereinigung über die gewählten Terms der anderen Ebene (innerhalb einer Ebene gilt
     * OR), Schnitt über mehrere einschränkende Ebenen (zwischen den Ebenen gilt AND). Die
     * eigene Auswahl geht nicht ein, sonst würde der erste Klick die Geschwister-Chips
     * ausgrauen. Gleiche Regel wie serverseitig in \Yours\CourseFilters.
     *
     * @return {Set|null} Erlaubte Werte, oder null wenn nichts einschränkt.
     */
    const availableFor = (group) => {
        const sets = [];

        groups.forEach((other) => {
            if (other === group) {
                return;
            }

            const selection = selectionOf(other);

            if (!selection.length) {
                return;
            }

            const union = new Set();

            selection.forEach((value) => {
                const implied = (map[group.mapName] || {})[value] || [];
                implied.forEach((item) => union.add(String(item)));
            });

            sets.push(union);
        });

        if (!sets.length) {
            return null;
        }

        return sets.reduce((carry, set) => new Set([...carry].filter((value) => set.has(value))));
    };

    const applyAvailability = () => {
        groups.forEach((group) => {
            const available = availableFor(group);

            group.chips.forEach((chip) => {
                // Ein gewählter Chip wird nie ausgegraut, sonst ließe er sich nicht mehr
                // abwählen. Die Kursliste darf dadurch leer sein.
                const isOk =
                    chip.getAttribute("aria-pressed") === "true" ||
                    available === null ||
                    available.has(valueOf(group, chip));

                chip.classList.toggle("is-unavailable", !isOk);

                if (isOk) {
                    chip.removeAttribute("aria-disabled");
                } else {
                    chip.setAttribute("aria-disabled", "true");
                }
            });
        });
    };

    const fetchCourses = () => {
        applyAvailability();
        syncInputs();

        if (pageInput) {
            pageInput.value = "1";
        }

        if (window.BBLMS && typeof window.BBLMS.fetchCourses === "function") {
            window.BBLMS.fetchCourses();
        }
    };

    /**
     * Sichtbare Adresse kürzen, ohne den History-Eintrag zu wechseln.
     *
     * BuddyBoss baut die neue Adresse aus dem serialisierten Formular und hängt
     * request_url in url-kodierter Form an. Das steht danach in jedem geteilten Link.
     * Der Pfad bleibt unangetastet, damit page/N/ aus der Pagination überlebt.
     */
    const tidyUrl = () => {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        params.delete("request_url");

        Array.from(params.keys()).forEach((key) => {
            if (params.get(key) === "") {
                params.delete(key);
            }
        });

        // Voreinstellungen tragen keine Information.
        if (params.get("current_page") === "1") {
            params.delete("current_page");
        }

        if (params.get("type") === "all") {
            params.delete("type");
        }

        const query = params.toString();

        return url.pathname + (query ? "?" + query : "") + url.hash;
    };

    /**
     * Kursliste und Trefferzahl im aktuellen History-Eintrag ablegen.
     *
     * BuddyBoss legt zwar selbst einen State an, aber erst ab dem ersten AJAX-Filter —
     * der Eintrag des Seitenaufrufs bleibt leer. Genau dort landet man beim ersten
     * Zurück-Klick. Ausserdem fehlt BuddyBoss' State die Trefferzahl. Deshalb schreiben
     * wir unter eigenem Schlüssel mit und lassen BuddyBoss' Daten unangetastet.
     */
    const snapshot = () => {
        if (!list) {
            return;
        }

        window.history.replaceState(
            {
                ...window.history.state,
                aeCourses: {
                    list: list.innerHTML,
                    count: counter ? counter.innerHTML : null,
                },
            },
            "",
            tidyUrl()
        );
    };

    const restore = (state) => {
        if (!state || !state.aeCourses) {
            return;
        }

        if (list) {
            list.innerHTML = state.aeCourses.list;
        }

        if (counter && null !== state.aeCourses.count) {
            counter.innerHTML = state.aeCourses.count;
        }
    };

    /**
     * Auswahl beider Ebenen aus der Adresse rekonstruieren.
     */
    const selectionFromUrl = () => {
        const params = new URLSearchParams(window.location.search);

        groups.forEach((group) => {
            const raw = params.get(group.param) || "";

            applySelection(
                group,
                raw
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean)
            );
        });

        applyAvailability();
        syncInputs();
    };

    container.addEventListener("click", (event) => {
        const chip = event.target.closest(".ae-course-chip");

        if (chip && container.contains(chip)) {
            const group = groups.filter((candidate) => candidate.chips.indexOf(chip) !== -1)[0];

            if (!group) {
                return;
            }

            // Unverfügbare Chips tragen aria-disabled statt disabled und bleiben damit
            // fokussierbar — der Klick muss hier verworfen werden.
            if (chip.getAttribute("aria-disabled") === "true") {
                return;
            }

            const isActive = chip.getAttribute("aria-pressed") === "true";

            chip.setAttribute("aria-pressed", isActive ? "false" : "true");
            chip.classList.toggle("is-active", !isActive);

            fetchCourses();

            return;
        }

        if (clearButton && event.target.closest(".ae-course-filters__clear")) {
            groups.forEach((group) => applySelection(group, []));

            fetchCourses();
        }
    });

    // Zurück/Vorwärts: BuddyBoss' eigener Handler läuft nie, weil er an
    // window.onpopstate hängt und von Borlabs Cookie überschrieben wird
    // (window.cmpmngr.observeURLChange). Filterzustand stellt BuddyBoss ohnehin nicht
    // wieder her — also machen wir hier alles selbst.
    window.addEventListener("popstate", (event) => {
        selectionFromUrl();
        restore(event.state);
    });

    // Jede von BuddyBoss nachgeladene Kursliste in den zugehörigen History-Eintrag
    // schreiben. BuddyBoss ruft pushState vor dem Austauschen des Markups auf, der
    // Eintrag ist hier also schon der richtige.
    if (list) {
        new MutationObserver(snapshot).observe(list, {
            childList: true,
            subtree: true,
        });
    }

    snapshot();
}
